import { inject, Injectable } from '@angular/core';
import { Observable, retry, throwError } from 'rxjs';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { ExceptionsErrorUrl, ExceptionsErrorType, ExceptionsErrorId } from './models';
import {
  AppRoutes,
  ErrorHandlingHeader,
  SKIP_ERROR,
  errorMessages,
  ErrorsModalData,
  NotificationsHttpService,
} from 'shared-utils';
import { translate } from 'translations-utils';
import { AdobeDataLayerService, LinkBuilderHttpService } from 'shared-data-access';
import { ModalErrorsComponent } from 'shared-ui';
import { NxDialogService, NxModalRef } from '@allianz/ng-aquila/modal';
import { TranslateService } from '@ngx-translate/core';
import { errorHandlingExceptions } from './consts/error-handling-exceptions.const';

@Injectable()
export class ErrorHandlingInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private notificationsHttpService = inject(NotificationsHttpService);
  private linkBuilderHttpService = inject(LinkBuilderHttpService);
  private dialogService: NxDialogService = inject(NxDialogService);
  private translateService: TranslateService = inject(TranslateService);
  private adobeDataLayerService: AdobeDataLayerService = inject(AdobeDataLayerService);

  private readonly ignoredErrorTypes: string[] = [
    'REJECTION',
    'DATA_MISMATCH',
    'MULTIPLE_MATCHES',
    'LIMIT_PACKAGE',
    'PRICE_CHANGED',
    'OFFERINGS_NOT_FOUND',
    'INVALID_DISCOUNT_CODE',
  ];

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const ignoreByException: boolean = errorHandlingExceptions[request.method]?.some((exception: RegExp) =>
      exception.test(request.url)
    );
    const ignoreError =
      !!request.headers.get(ErrorHandlingHeader.Ignore) || request.context.get(SKIP_ERROR) || ignoreByException;
    const retryCount = +request.headers.get(ErrorHandlingHeader.Retry) || 0;
    return next.handle(request).pipe(
      retry({ count: retryCount }),
      catchError((error: HttpErrorResponse) => {
        if (!ignoreError && this.checkModalExceptions(error)) {
          const errorsModalData: ErrorsModalData = {
            title: translate('portal.error-catching.name-content', { PH: error.name, PH_1: error.status }),
            traceId: error.error?.traceId,
            source: error.error?.source,
            message: error.error?.message,
            showErrorInfo: environment.config.errorInfo,
          };

          this.openModal(errorsModalData);
        }

        return throwError(() => error);
      })
    );
  }

  private checkModalExceptions(error: HttpErrorResponse): boolean {
    const status: number = +error.status;
    const type: string = error.error?.type;
    const url: string = error.url;

    const exceptionConditions: boolean[] = [
      this.isUnauthorizedError(status),
      this.isBadRequestWithIgnoredType(status, type),
      this.isNotFoundWithSpecificType(status, type),
      this.isBadRequestWithSpecificId(status, error.error?.id),
      this.isRouteMatchingError(status),
      this.isHealthCheckError(status, url),
      this.isBadRequestWithSpecificUrl(status, type, url),
      this.isAddressNotFoundError(status, type, url),
      this.isNotificationServiceError(url),
      this.isLinkBuilderError(status, url),
      this.isDirectQuoteExpiryError(status, type),
    ];

    return !exceptionConditions.some(Boolean);
  }

  private isUnauthorizedError(status: HttpStatusCode): boolean {
    return status === HttpStatusCode.Unauthorized;
  }

  private isBadRequestWithIgnoredType(status: HttpStatusCode, type: string): boolean {
    return status === HttpStatusCode.BadRequest && this.ignoredErrorTypes.includes(type);
  }

  private isNotFoundWithSpecificType(status: HttpStatusCode, type: string): boolean {
    return status === HttpStatusCode.NotFound && type === ExceptionsErrorType.QuotationNotFound;
  }

  private isBadRequestWithSpecificId(status: HttpStatusCode, id: string): boolean {
    return status === HttpStatusCode.BadRequest && id === ExceptionsErrorId.InvalidRiskFactors;
  }

  private isRouteMatchingError(status: HttpStatusCode): boolean {
    const routePatterns: AppRoutes[] = [
      AppRoutes.TravelPartners,
      AppRoutes.TravelPartnersBE,
      AppRoutes.SaraPartners,
      AppRoutes.BikePartners,
      AppRoutes.BikePartnersBE,
      AppRoutes.RsaPartners,
      AppRoutes.RsaPartnersBE,
    ];
    return (
      status === HttpStatusCode.BadRequest && routePatterns.some((route: AppRoutes) => this.router.url.includes(route))
    );
  }

  private isHealthCheckError(status: HttpStatusCode, url: string): boolean {
    return url?.includes(ExceptionsErrorUrl.HealthCheck) && status === HttpStatusCode.ServiceUnavailable;
  }

  private isBadRequestWithSpecificUrl(status: HttpStatusCode, type: string, url: string): boolean {
    return (
      [ExceptionsErrorUrl.Address, ExceptionsErrorUrl.Valid, ExceptionsErrorUrl.Vehicle].some((pattern: string) =>
        url?.includes(pattern)
      ) &&
      status === HttpStatusCode.BadRequest &&
      type === ExceptionsErrorType.NotFound
    );
  }

  private isAddressNotFoundError(status: HttpStatusCode, type: string, url: string): boolean {
    return (
      url.includes(ExceptionsErrorUrl.Address) &&
      status === HttpStatusCode.NotFound &&
      type === ExceptionsErrorType.NotFound
    );
  }

  private isNotificationServiceError(url: string): boolean {
    return url?.startsWith(this.notificationsHttpService.url);
  }

  private isLinkBuilderError(status: HttpStatusCode, url: string): boolean {
    return (
      url?.startsWith(this.linkBuilderHttpService.url) &&
      this.router.url.startsWith(`/${AppRoutes.Floating}`) &&
      status === HttpStatusCode.NotFound
    );
  }

  private isDirectQuoteExpiryError(status: HttpStatusCode, type: string): boolean {
    return status === HttpStatusCode.Conflict && type === ExceptionsErrorType.QuoteExpired;
  }

  private openModal(data: ErrorsModalData): NxModalRef<ModalErrorsComponent, void> {
    const message: string = errorMessages[data.message]
      ? this.translateService.instant(errorMessages[data.message])
      : data.message
        ? this.translateService.instant(data.message)
        : '';

    this.adobeDataLayerService.pushEvent('errorOccured', { message });

    data.mappedMessage = message;

    return this.dialogService.open(ModalErrorsComponent, {
      ariaLabel: data.title,
      disableClose: false,
      panelClass: 'custom-modal',
      data,
    });
  }
}
